import { useEffect, useState, memo, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Actions as UiActions } from "../../../redux/ui/reducers";
import "./left-section.scss";
import { UserTypeEnum } from "../../../utils/enums";
import RoleBasedAccess from "../../../components/role-based-access";
import UnassignedStudentCount from "../../../components/unassigned-student-count";
import useI18n from "../../../i18n/useI18n";

const assetStylesheets = [
  "/assets/plugins/bootstrap/css/bootstrap.min.css",
  "/assets/css/main.css",
  "/assets/css/color_skins.css",
];

const LeftSection = memo(() => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [openMenus, setOpenMenus] = useState({});
  const location = useLocation();
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.auth?.userInfo || {});
  const isMenuOpen = useSelector((state) => state.ui?.sidebarOpen ?? true);
  const { t } = useI18n();

  const handleSidebarToggle = useCallback(() => {
    dispatch(UiActions.toggleSidebar());
  }, [dispatch]);

  const handleMenuNavigation = useCallback(() => {
    if (window.innerWidth < 1150) {
      dispatch(UiActions.closeSidebar());
    }
  }, [dispatch]);

  // Auto close menu when screen width is below 1150px
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 1150) {
        if (!document.body.classList.contains('ls-toggle-menu')) {
          dispatch(UiActions.closeSidebar());
        }
      } else {
        if (document.body.classList.contains('ls-toggle-menu')) {
          dispatch(UiActions.openSidebar());
        }
      }
    };

    // Check on mount
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [dispatch]);

  // Redux state değiştiğinde body class'ını senkronize et
  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.remove('ls-toggle-menu');
    } else {
      document.body.classList.add('ls-toggle-menu');
    }
  }, [isMenuOpen]);

  useEffect(() => {
    const injectedLinks = assetStylesheets.map((href) => {
      const existing = document.querySelector(`link[data-sidebar-style="${href}"]`);

      if (existing) {
        return null;
      }

      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      link.setAttribute("data-sidebar-style", href);
      document.head.appendChild(link);
      return link;
    });

    return () => {
      injectedLinks.forEach((link) => {
        if (link && link.parentNode) {
          link.parentNode.removeChild(link);
        }
      });
    };
  }, []);

  const getFullName = () => {
    return `${userInfo.firstName || ''} ${userInfo.lastName || ''}`.trim() || t('SIDEBAR_USER_DEFAULT');
  };

  const getUserTypeLabel = () => {
    if (userInfo.userType === UserTypeEnum.AGENT) {
      return userInfo.agent?.companyName || t('SIDEBAR_USER_TYPE_AGENT');
    }

    const types = {
      [UserTypeEnum.STUDENT]: t('SIDEBAR_USER_TYPE_STUDENT'),
      [UserTypeEnum.TEACHER]: t('SIDEBAR_USER_TYPE_TEACHER'),
      [UserTypeEnum.ADMIN]: t('SIDEBAR_USER_TYPE_ADMIN'),
      [UserTypeEnum.AGENT]: t('SIDEBAR_USER_TYPE_AGENT'),
    };
    return types[userInfo.userType] || userInfo.userType || '';
  };

  const getDefaultAvatar = () => {
    if (userInfo.avatarUrl) {
      return userInfo.avatarUrl;
    }

    switch (userInfo.userType) {
      case UserTypeEnum.ADMIN:
        return '/assets/images/adminphoto.png';
      case UserTypeEnum.STUDENT:
        return '/assets/images/profile-photo.webp';
      case UserTypeEnum.AGENT:
        return '/assets/images/agentphoto.png';
      default:
        return '/assets/images/adminphoto.png';
    }
  };

  // Helper function to check if current path matches menu item
  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleTabClick = (e, tabName) => {
    e.preventDefault();
    setActiveTab(tabName);
  };

  const handleMenuToggle = (e, menuName) => {
    e.preventDefault();
    setOpenMenus((prev) => ({
      ...prev,
      [menuName]: !prev[menuName],
    }));
  };
  return (
    <>
      {/* Toggle Button - Shows when menu is closed */}
      {!isMenuOpen && (
        <button
          className="sidebar-toggle-btn closed"
          onClick={handleSidebarToggle}
          title={t('SIDEBAR_OPEN')}
        >
          <i className="zmdi zmdi-chevron-right"></i>
        </button>
      )}

      <aside id="leftsidebar" className="sidebar">
        {/* Toggle Button inside sidebar - positioned at top right */}
        {isMenuOpen && (
          <button
            className="sidebar-toggle-btn open inside-sidebar"
            onClick={handleSidebarToggle}
            title={t('SIDEBAR_CLOSE')}
          >
            <i className="zmdi zmdi-chevron-left"></i>
          </button>
        )}

        <ul className="nav nav-tabs">
          {/* <li className="nav-item">
          <a
            className={`nav-link ${activeTab === "dashboard" ? "active" : ""}`}
            href="#dashboard"
            onClick={(e) => handleTabClick(e, "dashboard")}
          >
            <i className="zmdi zmdi-home"></i>
          </a>
        </li> */}
          {/* <li className="nav-item">
          <a
            className={`nav-link ${activeTab === "user" ? "active" : ""}`}
            href="#user"
            onClick={(e) => handleTabClick(e, "user")}
          >
            {t('SIDEBAR_TAB_USER')}
          </a>
        </li> */}
        </ul>

        <div className="tab-content">
          <div
            className={`tab-pane stretchRight ${activeTab === "dashboard" ? "active" : ""}`}
            id="dashboard"
          >
            <div className="menu">
              <ul className="list text-left">
                <li>
                  <div className="user-info">
                    <div className="image">
                      <Link to="/profile" onClick={handleMenuNavigation}>
                        <img
                          src={getDefaultAvatar()}
                          alt="User"
                          onError={(e) => {
                            e.target.src = '/assets/images/profile-photo.webp';
                          }}
                        />
                      </Link>
                    </div>
                    <div className="detail">
                      <h4>{getFullName()}</h4>
                      <span>{getUserTypeLabel()}</span>
                    </div>
                  </div>
                </li>
                <li className="header" >{t('SIDEBAR_OVERVIEW')}</li>
                {/* <RoleBasedAccess allowedRoles={[UserTypeEnum.ADMIN, UserTypeEnum.AGENT]}> */}
                <li>
                  <Link
                    to="/dashboard"
                    className={isActive("/dashboard") ? "active" : ""}
                    onClick={handleMenuNavigation}
                  >
                    <i className="zmdi zmdi-home"></i>
                    <span>{t('SIDEBAR_DASHBOARD')}</span>
                  </Link>
                </li>
                <RoleBasedAccess allowedRoles={[UserTypeEnum.AGENT]}>
                  <li>
                    <Link
                      to="/profile?tab=contract"
                      className={location.pathname === "/profile" && location.search.includes("tab=contract") ? "active" : ""}
                      onClick={handleMenuNavigation}
                    >
                      <i className="zmdi zmdi-file-text"></i>
                      <span>{t('SIDEBAR_AGENT_CONTRACT')}</span>
                    </Link>
                  </li>
                </RoleBasedAccess>
                {/* </RoleBasedAccess> */}
                <li>
                  <Link
                    to="/profile"
                    className={location.pathname === "/profile" && !location.search.includes("tab=contract") ? "active" : ""}
                    onClick={handleMenuNavigation}
                  >
                    <i className="zmdi zmdi-account"></i>
                    <span>{t('SIDEBAR_PROFILE')}</span>
                  </Link>
                </li>
                <li className="header" >{t('SIDEBAR_ANNOUNCEMENTS')}</li>
                <li>
                  <Link
                    to="/announcement"
                    className={isActive("/announcement") ? "active" : ""}
                    onClick={handleMenuNavigation}
                  >
                    <i className="zmdi zmdi-notifications"></i>
                    <span>{t('SIDEBAR_ANNOUNCEMENTS')}</span>
                  </Link>
                </li>

                {/* <RoleBasedAccess allowedRoles={[UserTypeEnum.STUDENT]}>
                  <li>
                    <a href="#" onClick={(e) => { e.preventDefault(); alert('Hazırlanıyor...'); }}>
                      <i className="zmdi zmdi-settings"></i>
                      <span>{t('SIDEBAR_PREFERENCE_SYSTEM')}</span>
                    </a>
                  </li>
                  <li>
                    <a href="#" onClick={(e) => { e.preventDefault(); alert('Hazırlanıyor...'); }}>
                      <i className="zmdi zmdi-file-text"></i>
                      <span>{t('SIDEBAR_EXAM_SYSTEM')}</span>
                    </a>
                  </li>
                </RoleBasedAccess> */}
                <li className="header">{t('SIDEBAR_DIRECT_UNI_HEADER')}</li>
                <li>
                  <Link
                    to="/unimap"
                    className={isActive("/unimap") ? "active" : ""}
                    onClick={handleMenuNavigation}
                  >
                    <i className="zmdi zmdi-map"></i>
                    <span>{t('SIDEBAR_MAPS')}</span>
                  </Link>
                </li>

                <li>
                  <Link
                    to="/universities"
                    className={isActive("/universities") ? "active" : ""}
                    onClick={handleMenuNavigation}
                  >
                    <i className="zmdi zmdi-assignment"></i>
                    <span>{t('SIDEBAR_UNIVERSITY_LIST')}</span>
                  </Link>
                </li>
                <RoleBasedAccess allowedRoles={[UserTypeEnum.ADMIN]}>
                  <li>
                    <Link
                      to="/programs"
                      className={isActive("/programs") ? "active" : ""}
                      onClick={handleMenuNavigation}
                    >
                      <i className="zmdi zmdi-graduation-cap"></i>
                      <span>{t('SIDEBAR_PROGRAMS')}</span>
                    </Link>
                  </li>
                </RoleBasedAccess>
                <li>
                  <Link
                    to="/applications"
                    className={isActive("/applications") ? "active" : ""}
                    onClick={handleMenuNavigation}
                  >
                    <i className="zmdi zmdi-folder-outline"></i>
                    <span>{t('SIDEBAR_APPLICATIONS')}</span>
                  </Link>
                </li>
                <RoleBasedAccess allowedRoles={[UserTypeEnum.ADMIN, UserTypeEnum.STUDENT]}>
                  <li>
                    <Link
                      to="/preference-dates"
                      className={isActive("/preference-dates") ? "active" : ""}
                      onClick={handleMenuNavigation}
                    >
                      <i className="zmdi zmdi-calendar-check"></i>
                      <span>{t('SIDEBAR_PREFERENCE_DATES')}</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/registration-dates"
                      className={isActive("/registration-dates") ? "active" : ""}
                      onClick={handleMenuNavigation}
                    >
                      <i className="zmdi zmdi-calendar-note"></i>
                      <span>{t('SIDEBAR_REGISTRATION_DATES')}</span>
                    </Link>
                  </li>
                </RoleBasedAccess>
                <RoleBasedAccess allowedRoles={[UserTypeEnum.ADMIN, UserTypeEnum.AGENT]}>
                  <li>
                    <Link
                      to="/students"
                      className={isActive("/students") ? "active" : ""}
                      onClick={handleMenuNavigation}
                    >
                      <i className="zmdi zmdi-accounts"></i>
                      <span className="menu-item-with-badge">
                        <span>{t('SIDEBAR_STUDENTS')}</span>
                        {/* <UnassignedStudentCount /> */}
                      </span>
                    </Link>
                  </li>
                </RoleBasedAccess>
                <RoleBasedAccess allowedRoles={UserTypeEnum.ADMIN}>
                  <li>
                    <Link
                      to="/agents"
                      className={isActive("/agents") ? "active" : ""}
                      onClick={handleMenuNavigation}
                    >
                      <i className="zmdi zmdi-account-box"></i>
                      <span>{t('SIDEBAR_AGENTS')}</span>
                    </Link>
                  </li>
                </RoleBasedAccess>
                <RoleBasedAccess allowedRoles={UserTypeEnum.ADMIN}>
                  <li>
                    <Link
                      to="/agent-commissions"
                      className={isActive("/agent-commissions") ? "active" : ""}
                      onClick={handleMenuNavigation}
                    >
                      <i className="zmdi zmdi-money-box"></i>
                      <span>{t('SIDEBAR_AGENT_COMMISSIONS')}</span>
                    </Link>
                  </li>
                </RoleBasedAccess>

                {/* <li>
                <a href="#" >
                  <i className="zmdi zmdi-calendar-note"></i>
                  <span>Kayıt Tarihleri</span>
                </a>
              </li> */}


                <RoleBasedAccess allowedRoles={[UserTypeEnum.AGENT]}>
                  {(() => {
                    const agentId = userInfo.agent?.id || userInfo.id;
                    if (!agentId) return null;

                    const linkUrl = new URL('/auth/register', window.location.origin);
                    linkUrl.searchParams.set('usertype', 'student');
                    linkUrl.searchParams.set('agent', agentId);
                    const invitationLink = linkUrl.toString();

                    return (
                      <li className="invitation-link-item">
                        <div className="invitation-card">
                          <small className="invitation-title">
                            {t('SIDEBAR_INVITATION_LINK', 'Öğrenci Davet Linki')}
                          </small>
                          <div className="invitation-input-group">
                            <input
                              readOnly
                              value={invitationLink}
                            />
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(invitationLink);
                                const btn = document.getElementById('sidebar-copy-btn');
                                if (btn) {
                                  const originalHtml = btn.innerHTML;
                                  btn.innerHTML = '<i class="zmdi zmdi-check"></i>';
                                  setTimeout(() => {
                                    btn.innerHTML = originalHtml;
                                  }, 1500);
                                }
                              }}
                              id="sidebar-copy-btn"
                              className="btn btn-primary btn-sm btn-icon btn-copy"
                              title={t('COMMON_COPY', 'Kopyala')}
                            >
                              <i className="zmdi zmdi-copy"></i>
                            </button>
                          </div>
                        </div>
                      </li>
                    );
                  })()}
                </RoleBasedAccess>
                {/* <RoleBasedAccess allowedRoles={[UserTypeEnum.ADMIN, UserTypeEnum.AGENT]}>
                  <li>
                    <a href="#" onClick={(e) => { e.preventDefault(); alert('Hazırlanıyor...'); }}>
                      <i className="zmdi zmdi-folder"></i>
                      <span>{t('SIDEBAR_FILES')}</span>
                    </a>
                  </li>
                </RoleBasedAccess> */}
                {/* <li>
                  <a href="#" onClick={(e) => { e.preventDefault(); alert('Hazırlanıyor...'); }}>
                    <i className="zmdi zmdi-view-list"></i>
                    <span>{t('SIDEBAR_CLASS_SCHEDULES')}</span>
                  </a>
                </li> */}

                {/* <RoleBasedAccess allowedRoles={UserTypeEnum.ADMIN}>
                  <li>
                    <a href="#" onClick={(e) => { e.preventDefault(); alert('Hazırlanıyor...'); }}>
                      <i className="zmdi zmdi-money"></i>
                      <span>{t('SIDEBAR_PRICING')}</span>
                    </a>
                  </li>
                </RoleBasedAccess> */}
                {/* <li className="header">EKSTRA</li>
              <li>
                <div className="progress-container progress-primary m-t-10">
                  <span className="progress-badge">Bu Ayın Traffic</span>
                  <div className="progress">
                    <div
                      className="progress-bar progress-bar-warning"
                      role="progressbar"
                      aria-valuenow="67"
                      aria-valuemin="0"
                      aria-valuemax="100"
                      style={{ width: "67%" }}
                    >
                      <span className="progress-value">67%</span>
                    </div>
                  </div>
                </div>
                <div className="progress-container progress-info">
                  <span className="progress-badge">Sunucu Yükü</span>
                  <div className="progress">
                    <div
                      className="progress-bar progress-bar-warning"
                      role="progressbar"
                      aria-valuenow="86"
                      aria-valuemin="0"
                      aria-valuemax="100"
                      style={{ width: "86%" }}
                    >
                      <span className="progress-value">86%</span>
                    </div>
                  </div>
                </div>
              </li> */}
              </ul>
            </div>
          </div>

          <div
            className={`tab-pane stretchLeft ${activeTab === "user" ? "active" : ""}`}
            id="user"
          >
            <div className="menu">
              <ul className="list">
                <li>
                  <div className="user-info m-b-20 p-b-15">
                    <div className="image">
                      <Link to="/profile" onClick={handleMenuNavigation}>
                        <img
                          src={userInfo.avatarUrl || '/assets/images/profile-photo.webp'}
                          alt="User"
                          onError={(e) => {
                            e.target.src = '/assets/images/profile_av.jpg';
                          }}
                        />
                      </Link>
                    </div>
                    <div className="detail">
                      <h4>{getFullName()}</h4>
                      <small>{getUserTypeLabel()}</small>
                    </div>
                    <div className="row">
                      <div className="col-12">
                        <a title="youtube" href="https://www.youtube.com/@yosdanismanim">
                          <i className="zmdi zmdi-youtube"></i>
                        </a>
                        <a title="twitter" href="https://x.com/yosdanismanim">
                          <i className="zmdi zmdi-twitter"></i>
                        </a>
                        <a title="instagram" href="https://www.instagram.com/yosdanismanim/?igsh=MTZ4cTB3ejBwcDVqbg%3D%3D#">
                          <i className="zmdi zmdi-instagram"></i>
                        </a>
                      </div>
                      <div className="col-4 p-r-0">
                        <h5 className="m-b-5">13</h5>
                        <small>{t('SIDEBAR_STATS_EXP')}</small>
                      </div>
                      <div className="col-4">
                        <h5 className="m-b-5">33</h5>
                        <small>{t('SIDEBAR_STATS_AWARDS')}</small>
                      </div>
                      <div className="col-4 p-l-0">
                        <h5 className="m-b-5">237</h5>
                        <small>{t('SIDEBAR_STATS_CLASS')}</small>
                      </div>
                    </div>
                  </div>
                </li>
                <li>
                  <small className="text-muted">{t('SIDEBAR_EMAIL')}</small>
                  <p>{userInfo.email || '-'}</p>
                  <hr />
                  <small className="text-muted">{t('SIDEBAR_PHONE')}</small>
                  <p>{userInfo.phone || '-'}</p>
                  <hr />
                  {/* <ul className="list-unstyled">
                  <li>
                    <div>Photoshop</div>
                    <div className="progress m-b-20">
                      <div
                        className="progress-bar l-blue"
                        role="progressbar"
                        aria-valuenow="89"
                        aria-valuemin="0"
                        aria-valuemax="100"
                        style={{ width: "89%" }}
                      >
                        <span className="sr-only">89% Complete</span>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div>Illustrator</div>
                    <div className="progress m-b-20">
                      <div
                        className="progress-bar l-amber"
                        role="progressbar"
                        aria-valuenow="56"
                        aria-valuemin="0"
                        aria-valuemax="100"
                        style={{ width: "56%" }}
                      >
                        <span className="sr-only">56% Complete</span>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div>Art & Design</div>
                    <div className="progress m-b-20">
                      <div
                        className="progress-bar l-green"
                        role="progressbar"
                        aria-valuenow="78"
                        aria-valuemin="0"
                        aria-valuemax="100"
                        style={{ width: "78%" }}
                      >
                        <span className="sr-only">78% Complete</span>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div>HTML</div>
                    <div className="progress m-b-20">
                      <div
                        className="progress-bar l-blush"
                        role="progressbar"
                        aria-valuenow="43"
                        aria-valuemin="0"
                        aria-valuemax="100"
                        style={{ width: "56%" }}
                      >
                        <span className="sr-only">56% Complete</span>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div>CSS</div>
                    <div className="progress m-b-20">
                      <div
                        className="progress-bar l-parpl"
                        role="progressbar"
                        aria-valuenow="43"
                        aria-valuemin="0"
                        aria-valuemax="100"
                        style={{ width: "50%" }}
                      >
                        <span className="sr-only">50% Complete</span>
                      </div>
                    </div>
                  </li>
                </ul> */}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
});

export default LeftSection;
